import "../../css/vega-overrides/vega_overrides.scss";
import {
  ChartLayer,
  ChartSelection,
  VegaDataFrame,
  VegaLiteSpec,
  VegaMetadata,
  VegaSpec,
  isVegaSpec,
  patchVegaEscapeIssue,
  upgradeFromVegaLiteSpecV4toV5IfNecessary,
} from "@hex/common";
import { isEmpty } from "lodash";
import { rgba } from "polished";
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styled, { css, useTheme } from "styled-components";
import { LegendOrient, View } from "vega";
import vegaEmbed, { Result } from "vega-embed";

import { ChartLegend } from "../../chart/legend/ChartLegend";
import {
  didLegendRenderNull,
  getLegendOffsets,
  getLegendOrient,
  isAbsolutelyPositionedLegend,
  isHorizontalLegend,
} from "../../chart/legend/chartLegendUtils";
import { useCompositeRef } from "../../hooks/useCompositeRef";
import { useDebouncedCallback } from "../../hooks/useDebouncedCallback";
import { useResizeSensor } from "../../hooks/useResizeSensor";

import { HorizontalLegendContainer } from "./HorizontalLegendContainer";
import { useHydratedVegaLiteSpec } from "./utils";
import { customVegaInitialization } from "./vegaInitialization";

const VerticalLegendContainer = styled.div<{ $absolute?: boolean }>`
  flex-shrink: 1;
  max-height: calc(100% - 40px);
  margin-top: 8px;
  margin-left: 8px;
  scrollbar-gutter: stable;

  position: ${({ $absolute }) => ($absolute ? "absolute" : "static")};

  ${({ $absolute }) =>
    $absolute &&
    css`
      margin-top: 0;
      margin-bottom: 0;
      padding: 4px;
      overflow-y: auto;
      background-color: ${({ theme }) =>
        rgba(theme.backgroundColor.DEFAULT, 0.5)};
      box-shadow: ${({ theme }) => theme.boxShadow.CARD};

      max-height: 140px;
      transition: ${({ theme }) =>
        `max-height ${theme.animation.duration} ${theme.animation.easing}, background-color ${theme.animation.duration} ${theme.animation.easing}`};

      &:hover {
        max-height: calc(100% - 140px);

        background-color: ${({ theme }) => theme.backgroundColor.DEFAULT};
        box-shadow: ${({ theme }) => theme.boxShadow.CARD};
      }
    `}
`;

const ChartContainer = styled.div<{
  $orient?: LegendOrient;
  $min_height?: number;
  $min_width?: number;
}>`
  display: flex;
  width: 100%;
  height: 100%;
  min-height: ${({ $min_height }) =>
    $min_height != null ? `${$min_height}px` : "100%"};
  min-width: ${({ $min_width }) =>
    $min_width != null ? `${$min_width}px` : "100%"};
  overflow-y: hidden;
  position: relative;

  ${({ $orient }) => css`
    flex-direction: ${$orient === "right"
      ? "row"
      : $orient === "left"
      ? "row-reverse"
      : $orient === "top"
      ? "column-reverse"
      : $orient === "bottom"
      ? "column"
      : "row"};

    path[role="graphics-symbol"] {
      transition: opacity ${({ theme }) => theme.animation.durationMedium}
        ${({ theme }) => theme.animation.easing};
    }
  `}

  /* See https://github.com/altair-viz/altair/issues/1734 */
  canvas.marks {
    max-width: 100% !important;
    height: auto !important;
  }

  svg.marks {
    max-width: 100% !important;
    height: auto !important;
  }
`;

export const VegaChartRendererContainer = ChartContainer;

export interface VegaChartRendererProps {
  readonly className?: string;
  readonly vegaLiteSpec: VegaLiteSpec;
  readonly dataFrames: VegaDataFrame[];
  readonly metadata: VegaMetadata | null;
}

export const VegaChartRenderer: React.ComponentType<VegaChartRendererProps> =
  React.memo(function VegaChartRenderer({
    className,
    dataFrames,
    metadata,
    vegaLiteSpec,
  }) {
    const theme = useTheme();
    const updatedVegaLiteSpec = useMemo(
      () => upgradeFromVegaLiteSpecV4toV5IfNecessary(vegaLiteSpec),
      [vegaLiteSpec],
    );
    const hydratedVegaLiteSpec = useHydratedVegaLiteSpec({
      vegaLiteSpec: updatedVegaLiteSpec,
      metadata,
      dataFrames,
      displayOverrides: {
        fontFamily: theme.fontFamily.DEFAULT,
        background: theme.backgroundColor.DEFAULT,
        labelColor: theme.fontColor.DEFAULT,
        domainColor: theme.borderColor.MUTED,
        gridColor: theme.borderColor.MUTED,
        tickColor: theme.borderColor.MUTED,
        titleColor: theme.fontColor.DEFAULT,
      },
    });

    return (
      <HydratedVegaChartRenderer
        className={className}
        hydratedVegaSpec={hydratedVegaLiteSpec}
      />
    );
  });

export interface HydratedVegaChartRendererProps {
  readonly className?: string;
  readonly hydratedVegaSpec: VegaLiteSpec | VegaSpec;
  onVegaUpdate?: (result?: Result) => void;
  renderMode?: "svg" | "canvas" | "hybrid";
  selection?: ChartSelection | null;
  onLayerChange?: (layer: ChartLayer) => void;
}

export const HydratedVegaChartRenderer: React.ComponentType<HydratedVegaChartRendererProps> =
  React.memo(function HydratedVegaChartRenderer({
    className,
    hydratedVegaSpec,
    onLayerChange,
    onVegaUpdate,
    renderMode,
    selection,
  }) {
    const legendOrient = isVegaSpec(hydratedVegaSpec)
      ? getLegendOrient(hydratedVegaSpec)
      : undefined;

    const renderedAtLeastOnce = useRef(false);
    const chartContainerRef = useRef<HTMLDivElement | null>(null);
    // We use this to track our latest vega embed result
    // so we can call `finalize()` on it in order to clean it up and prevent memory leaks.
    // Whenever we have a new vega embed result, we should clean up the previous on stored here
    // before storing the new one.
    const latestResult = useRef<Result | null>(null);
    const [error, setError] = useState<Error>();
    const [view, setView] = useState<View | null>(null);
    const theme = useTheme();

    useLayoutEffect(() => {
      customVegaInitialization();
    }, []);

    // TODO: clean this up and move to a separate component for handling
    // custom legend integration in chart
    let legend: JSX.Element | undefined;
    let isCustomLegendRendered = false;
    if (isVegaSpec(hydratedVegaSpec)) {
      legend = (
        <ChartLegend
          selection={selection}
          spec={hydratedVegaSpec}
          view={view}
          onLayerChange={onLayerChange}
        />
      );
      isCustomLegendRendered = !didLegendRenderNull(legend, theme);
    }

    const resizeChart = useCallback((v: View | null, heightDelta = 0) => {
      // initial render resizes the chart as well, so avoid that by not
      // dispatching the event until we have rendered at least once
      if (renderedAtLeastOnce.current && !isWindowResizingRef.current) {
        requestAnimationFrame(() => {
          if (v != null) {
            const { clientHeight, clientWidth } =
              chartContainerRef.current ?? {};
            void v
              .width(clientWidth ?? v.width())
              .height((clientHeight ?? v.height()) + heightDelta)
              .runAsync(); // re-render the chart in the new size
          }
        });
      }
    }, []);

    const onResize = useCallback(() => {
      resizeChart(view);
    }, [resizeChart, view]);
    const debouncedOnResize = useDebouncedCallback(onResize, 200);

    const resizeRef = useResizeSensor({ onResize: debouncedOnResize });
    const compositeRef = useCompositeRef(chartContainerRef, resizeRef);

    const retryCountRef = useRef(0);
    const retryTimeoutRef = useRef<number | null>(null);
    useEffect(() => {
      // clear retry timeout if component is unmounted
      return () => {
        if (retryTimeoutRef.current != null) {
          clearTimeout(retryTimeoutRef.current);
        }
      };
    }, []);

    const updateVegaEmbed = useCallback((): void => {
      const chartContainerElement = chartContainerRef.current;
      if (chartContainerElement == null) {
        return;
      }

      // SUP-794: users reported a bug where pivoted charts would not render on initial project load
      // and would require re-running the cell in order for it to render. There are two issues here:
      // (1) when initially rendering the chart on project load, the chart container DOM element may
      // or may not have fully resized to fit its allotted space via css flex-grow, and could have
      // 0 width and height. This usually isn't a problem because when the container is resized, we
      // resize the chart to fit the new container size. However, this leads to our second issue...
      // (2) when we try to render a 0 width/height vega chart that contains voronoi transforms
      // it runs into an error because while computing paths from voronoi polygons it runs into a
      // NPE when the polygon is a single point. (Pushed fix here: https://github.com/vega/vega/pull/3768)
      // This leaves the chart in a broken state, and it doesn't get fixed unless the chart is re-rendered
      // from scratch.
      // To fix this, we check if the chart container has 0 width and height and uses voronoi transforms,
      // and if so, we wait a bit to retry rendering the chart. We retry up to 3 times.
      if (!renderedAtLeastOnce.current) {
        const hasVoronoiTransform =
          isVegaSpec(hydratedVegaSpec) &&
          hydratedVegaSpec.marks?.find(
            (m) =>
              m.type === "path" &&
              m.name != null &&
              m.name?.toLowerCase().includes("voronoi"),
          ) != null;
        if (hasVoronoiTransform) {
          // use another if to avoid getBoundingClientRect unless needed to avoid browser layout
          const { height, width } =
            chartContainerElement.getBoundingClientRect();
          if (width === 0 && height === 0) {
            const timeoutMillis =
              // exponential backoff after 3 retries
              2 ** Math.max(retryCountRef.current - 2, 0) * 100;
            retryCountRef.current += 1;
            retryTimeoutRef.current = window.setTimeout(
              updateVegaEmbed,
              timeoutMillis,
            );
            return;
          }
        }
      }

      const patchedVegaSpec = isCustomLegendRendered
        ? patchVegaEscapeIssue({
            ...hydratedVegaSpec,
            legends: undefined,
          })
        : patchVegaEscapeIssue(hydratedVegaSpec);

      vegaEmbed(chartContainerElement, patchedVegaSpec, {
        actions: false,
        config: {
          customFormatTypes: true,
        },
        tooltip: {
          theme: theme.vegaTooltipTheme,
        },
        // Allow use of "hybrid" renderer, which isn't in vega-typings yet.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        renderer: renderMode ?? ("canvas" as any),
      })
        .then((r) => {
          if (latestResult.current != null) {
            latestResult.current.finalize();
          }
          latestResult.current = r;

          setView(r.view);
          r.view.resize();

          // this is explicitly set before the `onResize` call below because that function
          // will check this value to determine whether or not to resize the chart
          renderedAtLeastOnce.current = true;
          // https://vega.github.io/vega-lite/docs/size.html#width-and-height-of-multi-view-displays
          // multi-view displays such as concat/faceted/repeated charts will ignore
          // the width/height of the container element and sometimes we end up with
          // a view width/height of 0. In this case, we manually call `onResize` to
          // resize the chart to the width/height of the container.
          if (r.view.width() === 0 && r.view.height() === 0) {
            resizeChart(r.view);
          }

          setError(undefined);
          onVegaUpdate?.(r);
          return r;
        })
        .catch((err) => {
          renderedAtLeastOnce.current = true;
          setError(err);
          return undefined;
        })
        .finally(() => {
          retryCountRef.current = 0;
          retryTimeoutRef.current = null;
        });
    }, [
      isCustomLegendRendered,
      hydratedVegaSpec,
      theme.vegaTooltipTheme,
      renderMode,
      onVegaUpdate,
      resizeChart,
    ]);

    // While leading: true might be nice to use here, there are cases where some
    // parameters can cause significant load on customers' browsers. By taking
    // this route we can ensure they can type in correct parameters to avoid the
    // potential lock-up.
    const debouncedUpdateVegaEmbed = useDebouncedCallback(
      updateVegaEmbed,
      300,
      {
        maxWait: 1000,
        onUnmount: "cancel",
      },
    );

    useLayoutEffect(() => {
      debouncedUpdateVegaEmbed();
      // Because this is debounced, we don't return a clean up function here.
      // Instead, subsequent calls to `updateVegaEmbed` clean up after the previous call.
      // Our effect below handles the final case of component unmount
    }, [hydratedVegaSpec, debouncedUpdateVegaEmbed, theme.vegaTooltipTheme]);

    useLayoutEffect(() => {
      return () => {
        if (latestResult.current != null) {
          latestResult.current.finalize();
          latestResult.current = null;
        }
      };
    }, []);

    // keep track of when the window is resizing so that we don't run
    // view.runAsync() while the window is resizing.
    const isWindowResizingRef = useRef(false);
    useEffect(() => {
      let resizeTimeout: number | undefined;
      const onWindowResize = (): void => {
        isWindowResizingRef.current = true;
        clearTimeout(resizeTimeout);
        resizeTimeout = window.setTimeout(() => {
          isWindowResizingRef.current = false;
        }, 100);
      };
      window.addEventListener("resize", onWindowResize);
      return () => {
        window.removeEventListener("resize", onWindowResize);
      };
    }, []);

    const isLegendAbsolutelyPositioned =
      isAbsolutelyPositionedLegend(legendOrient);
    const legendOffsets = getLegendOffsets(view, legendOrient);

    // For charts that should scroll when they shrink below a certain size,
    // we add min_height and min_width signals to the Vega spec (in the chart
    // translator) that compute these minimum dimensions.
    let min_height;
    try {
      min_height = view?.signal("min_height");
    } catch (_) {
      // No min_height
    }
    let min_width;
    try {
      min_width = view?.signal("min_width");
    } catch (_) {
      // No min_width
    }
    return (
      <ChartContainer
        $orient={legendOrient}
        className={className}
        $min_height={min_height}
        $min_width={min_width}
      >
        <div
          ref={compositeRef}
          css={`
            flex-grow: 1;
            flex-shrink: 1;
          `}
        >
          {error?.message}
        </div>
        {legend != null &&
          isCustomLegendRendered &&
          (!isLegendAbsolutelyPositioned || !isEmpty(legendOffsets)) &&
          (isHorizontalLegend(legendOrient) ? (
            <HorizontalLegendContainer resizeChart={resizeChart} view={view}>
              {legend}
            </HorizontalLegendContainer>
          ) : (
            <VerticalLegendContainer
              $absolute={isLegendAbsolutelyPositioned}
              style={legendOffsets}
            >
              {legend}
            </VerticalLegendContainer>
          ))}
      </ChartContainer>
    );
  });

export default VegaChartRenderer;
